import { motion, AnimatePresence } from 'framer-motion'

const AGENT_CONTENT = {
  'Orchestrator Agent': {
    attention: [
      { label: 'PO Approval', title: '3 POs overdue for approval', description: 'Combined value $48K — SLA breached', cta: 'Approve All' },
      { label: 'Quantity Change', title: 'Supplier reduced qty on PO#4821', description: 'Trocar shortfall — critical surgical SKU', cta: 'Review' },
      { label: 'Invoice Mismatch', title: 'Price deviation on 3 line items', description: '$2,100 over contracted rates', cta: 'Investigate' },
    ],
    activity: [
      { name: 'Status & Tracking', task: 'Reconciling shipment ETA for delayed orders', status: 'Processing' },
      { name: 'Creation & Replenishment', task: 'Generating replenishment orders for 6 SKUs', status: 'Active' },
      { name: 'Validation', task: 'Validating pricing on 3 pending POs', status: 'Active' },
      { name: 'Exception Resolution', task: 'Analyzing invoice mismatch on PO#4815', status: 'Active' },
      { name: 'PO-to-Invoice Matching', task: 'Matching 8 invoices to PO receipts', status: 'Active' },
    ],
  },
  'Status & Tracking': {
    attention: [
      { label: 'Delayed Shipment', title: 'PO#4821 delayed 2 days', description: 'Carrier rerouting due to weather — OR impact Jun 21', cta: 'Expedite' },
      { label: 'Missing ACK', title: 'PO#4825 unacknowledged 48hrs', description: 'Supplier portal timeout — EDI fallback active', cta: 'Follow Up' },
    ],
    activity: [
      { name: 'ETA Reconciliation', task: 'Updated ETAs on 12 active POs', status: 'Active' },
      { name: 'Carrier Monitoring', task: 'Watching 3 in-transit shipments', status: 'Active' },
      { name: 'ACK Retry', task: 'Retrying EDI acknowledgement for PO#4825', status: 'Processing' },
      { name: 'Delay Alerts', task: 'Notified OR scheduler of Jun 21 risk', status: 'Active' },
    ],
  },
  'Creation & Replenishment': {
    attention: [
      { label: 'Critical Stock', title: 'Endoscopic Trocar 5mm — 4 days left', description: 'Below safety stock — replenishment urgent', cta: 'Create PO' },
      { label: 'Pending Approval', title: '2 replenishment POs awaiting sign-off', description: 'Combined value $44K — 1.4 day avg wait', cta: 'Approve' },
    ],
    activity: [
      { name: 'PO Generation', task: 'Drafted 3 replenishment orders today', status: 'Active' },
      { name: 'PAR Monitoring', task: 'Monitoring 42 SKUs across 8 facilities', status: 'Active' },
      { name: 'Demand Sync', task: 'Syncing with OR schedule for Jun 21–28', status: 'Processing' },
      { name: 'Supplier Routing', task: 'Routing orders to preferred J&J contracts', status: 'Active' },
    ],
  },
  'Validation': {
    attention: [
      { label: 'Pricing Discrepancy', title: '3 line items 4–7% over contract', description: 'Contract pricing table not refreshed post-renewal', cta: 'Fix Prices' },
      { label: 'Duplicate Risk', title: 'PO#4798 mirrors PO#4761', description: 'Same items, supplier, and week — auto-consolidation blocked', cta: 'Review' },
    ],
    activity: [
      { name: 'Price Validation', task: 'Validated 14 POs against contract rates', status: 'Active' },
      { name: 'Duplicate Check', task: '1 duplicate risk flagged for approval', status: 'Active' },
      { name: 'Contract Sync', task: 'Refreshing Jan renewal pricing table', status: 'Processing' },
      { name: 'Approval Routing', task: 'Routing 3 flagged POs to procurement', status: 'Active' },
    ],
  },
  'Exception Resolution': {
    attention: [
      { label: 'Invoice Mismatch', title: 'INV-2887 — $1,240 discrepancy', description: 'Unit price updated post-PO without amendment', cta: 'Escalate' },
      { label: 'Backorder', title: '4 SKUs with no supplier ETA', description: 'Alternate supplier PO activation recommended', cta: 'Activate' },
    ],
    activity: [
      { name: 'Invoice Exception', task: 'Escalated INV-2887 to AP team', status: 'Active' },
      { name: 'Backorder Mgmt', task: 'Identifying alternate suppliers for 4 SKUs', status: 'Processing' },
      { name: 'Exception Log', task: '2 exceptions resolved today', status: 'Active' },
      { name: 'SLA Monitoring', task: 'Tracking resolution time on 3 open cases', status: 'Active' },
    ],
  },
  'PO-to-Invoice Matching': {
    attention: [
      { label: 'Mismatch', title: 'INV-2887 has $1,240 discrepancy vs PO#4815', description: 'Root cause: unit price updated post-PO', cta: 'Investigate' },
      { label: 'Pending Match', title: '2 invoices awaiting 3-way match', description: 'GRN not yet confirmed in system', cta: 'Confirm GRN' },
    ],
    activity: [
      { name: '3-Way Matching', task: 'Matched 8 invoices to PO receipts today', status: 'Active' },
      { name: 'Auto-Approval', task: '6 invoices auto-approved within tolerance', status: 'Active' },
      { name: 'Discrepancy Flag', task: '1 exception raised to finance team', status: 'Processing' },
      { name: 'GRN Sync', task: 'Awaiting 2 goods receipt confirmations', status: 'Processing' },
    ],
  },
}

const FALLBACK = AGENT_CONTENT['Orchestrator Agent']

const labelStyle = {
  fontSize: 10,
  fontWeight: 700,
  color: '#A39992',
  textTransform: 'uppercase',
  letterSpacing: '0.09em',
}

export default function POSidePanel({ selectedAgent }) {
  const content = AGENT_CONTENT[selectedAgent] ?? FALLBACK

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 24, overflowY: 'auto', padding: 16 }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedAgent}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
        >
          {/* Needs Attention */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 28 }}>
              <span style={labelStyle}>Needs Attention</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#f8f8f7', background: 'rgba(235,23,0,0.35)', borderRadius: 3, padding: '1px 6px' }}>
                {content.attention.length}
              </span>
            </div>
            {content.attention.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 4, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={labelStyle}>{item.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.4 }}>{item.title}</span>
                  <span style={{ fontSize: 12, color: '#D5CFC9', lineHeight: 1.4 }}>{item.description}</span>
                </div>
                <button style={{ background: '#EB1700', border: 'none', borderRadius: 8, padding: '4px 10px', color: 'white', fontSize: 12, fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}>
                  {item.cta}
                </button>
              </motion.div>
            ))}
          </div>

          {/* Agent Activity */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 28 }}>
              <span style={labelStyle}>Agent Activity</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#f8f8f7', background: 'rgba(255,255,255,0.1)', borderRadius: 3, padding: '1px 6px' }}>
                {content.activity.length}
              </span>
            </div>
            {content.activity.map((agent, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.15 + i * 0.05 }}
                style={{ background: 'rgba(0,0,0,0.1)', borderRadius: 4, padding: '9px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <span style={labelStyle}>{agent.name}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.4 }}>{agent.task}</span>
                <span style={{ fontSize: 11, color: agent.status === 'Active' ? '#4ADE80' : 'rgba(251,191,36,0.9)' }}>{agent.status}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  )
}
